import React, { useState, useEffect, useCallback } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import {
    X, Phone, Package, CheckCircle2,
    IndianRupee, Wallet, Info, Clock, Timer,
    MapPin, Loader2, Banknote
} from "lucide-react";


import {fetchDeliveryPartnerDetail} from "../service/api/orders";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});


const makeIcon = (heading, color = "#6366f1") => {
    const deg = isNaN(Number(heading)) ? 0 : Number(heading);
    const uid = Math.random().toString(36).slice(2, 6);
    // Pin never rotates — only the emoji inside spins by heading.
    return L.divIcon({
        html: `
        <div style="width:42px;height:52px;">
            <svg viewBox="0 0 42 52" width="42" height="52" xmlns="http://www.w3.org/2000/svg">
                <filter id="rs-${uid}">
                    <feDropShadow dx="0" dy="3" stdDeviation="3" flood-color="${color}" flood-opacity="0.35"/>
                    <feDropShadow dx="0" dy="1" stdDeviation="1" flood-opacity="0.15"/>
                </filter>
                <path d="M21 2C12.16 2 5 9.16 5 18c0 11.5 16 31 16 31S37 29.5 37 18C37 9.16 29.84 2 21 2z"
                    fill="${color}" filter="url(#rs-${uid})"/>
                <circle cx="21" cy="17" r="11" fill="white" opacity="0.92"/>
                <circle cx="21" cy="17" r="11" fill="none" stroke="${color}" stroke-width="1.2" opacity="0.3"/>
                <!-- foreignObject fixed to pin; emoji rotates inside it by heading -->
                <foreignObject x="7" y="5" width="28" height="26">
                    <div xmlns="http://www.w3.org/1999/xhtml"
                        style="width:28px;height:26px;display:flex;align-items:center;justify-content:center;font-size:14px;line-height:1;user-select:none;transform:rotate(${deg}deg);transform-origin:center;">
                        🛵
                    </div>
                </foreignObject>
            </svg>
        </div>`,
        className: "",
        iconSize:    [42, 52],
        iconAnchor:  [21, 50],
        popupAnchor: [0, -54],
    });
};


const StatPill = ({ icon: Icon, label, value, color, bg }) => (
    <div style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "11px 14px", borderRadius: 11,
        background: bg, border: `1px solid ${color}22`,
        minWidth: 0,
    }}>
        <div style={{
            width: 36, height: 36, borderRadius: 9,
            background: `${color}18`,
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
            <Icon size={17} color={color} />
        </div>
        <div style={{ minWidth: 0 }}>
            <div style={{
                fontSize: "0.95rem", fontWeight: 800, color: "#0f172a",
                lineHeight: 1.1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
                {value}
            </div>
            <div style={{
                fontSize: "0.65rem", fontWeight: 600, color: "#94a3b8",
                marginTop: 2, letterSpacing: "0.04em", textTransform: "uppercase",
            }}>
                {label}
            </div>
        </div>
    </div>
);

// Helper — formats seconds → "2h 15m" / "45m" / "< 1m"
const formatOnlineTime = (seconds) => {
    if (!seconds || seconds < 60) return '< 1m';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
};

// Helper — formats "2026-03-13 08:30:00" → "8:30 AM"
const formatOnlineAt = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleTimeString('en-IN', {
        hour: 'numeric', minute: '2-digit', hour12: true,
    });
};

const DeliveryPartnerModal = ({ partner, onClose }) => {
    const [detail, setDetail]   = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState(null);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await fetchDeliveryPartnerDetail(partner.id);
            setDetail(data?.data);
        } catch (e) {
            setError("Could not load partner details.");
        } finally {
            setLoading(false);
        }
    }, [partner.id]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const handler = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", handler);
        return () => document.removeEventListener("keydown", handler);
    }, [onClose]);

    const lat = partner.latitude;
    const lng = partner.longitude;
    const hasLocation = lat && lng;

    const isOnline          = detail?.status === 1;
    const onlineAtFormatted = formatOnlineAt(detail?.online_at);
    const onlineTimeStr     = formatOnlineTime(detail?.total_online_seconds);

    return (
        <>
            <style>{`
                @keyframes modal-backdrop-in  { from{opacity:0}  to{opacity:1} }
                @keyframes modal-slide-in     { from{opacity:0;transform:translate(-50%,-50%) translateY(20px) scale(0.97)} to{opacity:1;transform:translate(-50%,-50%) translateY(0) scale(1)} }
                @keyframes modal-sheet-in     { from{transform:translateY(100%)} to{transform:translateY(0)} }
                @keyframes spin               { to{transform:rotate(360deg)} }
                @keyframes pulse-dot          { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.5;transform:scale(1.4)} }

                .partner-modal-scroll::-webkit-scrollbar       { width:5px }
                .partner-modal-scroll::-webkit-scrollbar-track { background:transparent }
                .partner-modal-scroll::-webkit-scrollbar-thumb { background:#e2e8f0; border-radius:4px }

                .partner-modal-box {
                    position:fixed; top:50%; left:50%;
                    transform:translate(-50%,-50%);
                    width:min(600px, 96vw); max-height:92vh;
                    background:#fff; border-radius:20px;
                    box-shadow:0 25px 60px rgba(0,0,0,0.2),0 8px 20px rgba(0,0,0,0.1);
                    z-index:10001; overflow:hidden; display:flex; flex-direction:column;
                    animation:modal-slide-in 0.25s cubic-bezier(.22,1,.36,1) forwards;
                    font-family:'Inter',sans-serif;
                }
                .stat-pills-grid {
                    display:grid; grid-template-columns:repeat(2,1fr);
                    gap:10px; padding:16px 16px 0;
                }
                @media (max-width:520px) {
                    .partner-modal-box {
                        top:auto !important; bottom:0 !important; left:0 !important;
                        transform:none !important; width:100vw !important;
                        max-height:90dvh !important; border-radius:18px 18px 0 0 !important;
                        animation:modal-sheet-in 0.3s cubic-bezier(.22,1,.36,1) forwards !important;
                    }
                }
            `}</style>

            {/* Backdrop */}
            <div onClick={onClose} style={{
                position:"fixed", inset:0,
                background:"rgba(15,23,42,0.55)",
                backdropFilter:"blur(4px)",
                zIndex:10000,
                animation:"modal-backdrop-in 0.2s ease forwards",
            }} />

            <div className="partner-modal-box">

                {/* ── Header ── */}
                <div style={{
                    padding:"16px 18px",
                    borderBottom:"1px solid #f1f5f9",
                    display:"flex", alignItems:"center", justifyContent:"space-between",
                    flexShrink:0,
                }}>
                    <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                        {partner?.photo ? (
                            <img
                                src={"https://food.totalfresh.co/assets/img/delivery/" + partner?.photo}
                                alt="Delivery guy"
                                style={{ width:48, height:48, borderRadius:12, objectFit:"cover", border:"2px solid #e0e7ff" }}
                            />
                        ) : (
                            <div style={{
                                width:48, height:48, borderRadius:12,
                                background:"linear-gradient(135deg,#6366f1,#818cf8)",
                                display:"flex", alignItems:"center", justifyContent:"center",
                                fontSize:22, boxShadow:"0 4px 12px rgba(99,102,241,0.3)",
                            }}>🛵</div>
                        )}
                        <div>
                            <div style={{ fontSize:"1rem", fontWeight:800, color:"#0f172a", lineHeight:1.2 }}>
                                {detail?.name ?? partner.name}
                            </div>
                            {(detail?.phone ?? partner?.phone) && (
                                <a href={`tel:${detail?.phone ?? partner?.phone}`} style={{
                                    fontSize:"0.75rem", color:"#6f6f6f",
                                    display:"flex", alignItems:"center", gap:4,
                                    textDecoration:"none", marginTop:2,
                                }}>
                                    <Phone size={11} color="#6f6f6f" />
                                    {detail?.phone ?? partner?.phone}
                                </a>
                            )}

                            {/* ── Online status + tracking row ── */}
                            {!loading && (
                                <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:5, flexWrap:"wrap" }}>

                                    {/* Online / Offline badge */}
                                    <span style={{
                                        display:"inline-flex", alignItems:"center", gap:5,
                                        padding:"2px 8px", borderRadius:20,
                                        background: isOnline ? "#dcfce7" : "#f1f5f9",
                                        fontSize:"0.7rem", fontWeight:700,
                                        color: isOnline ? "#15803d" : "#64748b",
                                    }}>
                                        <span style={{
                                            width:6, height:6, borderRadius:"50%",
                                            background: isOnline ? "#22c55e" : "#94a3b8",
                                            display:"inline-block",
                                            animation: isOnline ? "pulse-dot 1.5s ease-in-out infinite" : "none",
                                        }} />
                                        {isOnline ? "Online" : "Offline"}
                                    </span>

                                    {/* Since what time */}
                                    {onlineAtFormatted && (
                                        <span style={{
                                            fontSize:"0.7rem", color:"#94a3b8", fontWeight:500,
                                            display:"flex", alignItems:"center", gap:3,
                                        }}>
                                            <Clock size={10} color="#94a3b8" />
                                            since {onlineAtFormatted}
                                        </span>
                                    )}

                                    {/* Total online time today */}
                                    {detail?.total_online_seconds > 0 && (
                                        <span style={{
                                            fontSize:"0.7rem", color:"#6366f1", fontWeight:600,
                                            display:"flex", alignItems:"center", gap:3,
                                            background:"#eef2ff", padding:"2px 7px", borderRadius:20,
                                        }}>
                                            <Timer size={10} color="#6366f1" />
                                            {onlineTimeStr} today
                                        </span>
                                    )}

                                </div>
                            )}
                        </div>
                    </div>

                    <button
                        onClick={onClose}
                        style={{
                            width:34, height:34, borderRadius:9,
                            background:"#f8fafc", border:"1px solid #e2e8f0",
                            display:"flex", alignItems:"center", justifyContent:"center",
                            cursor:"pointer", flexShrink:0,
                        }}
                        onMouseEnter={e => e.currentTarget.style.background="#f1f5f9"}
                        onMouseLeave={e => e.currentTarget.style.background="#f8fafc"}
                    >
                        <X size={15} color="#475569" />
                    </button>
                </div>

                {/* ── Body ── */}
                <div className="partner-modal-scroll" style={{ overflowY:"auto", flex:1 }}>

                    {loading && (
                        <div style={{
                            padding:"60px 20px", display:"flex", flexDirection:"column",
                            alignItems:"center", gap:12, color:"#94a3b8",
                        }}>
                            <Loader2 size={28} color="#6366f1" style={{ animation:"spin 1s linear infinite" }} />
                            <span style={{ fontSize:"0.85rem", fontWeight:600 }}>Loading partner info…</span>
                        </div>
                    )}

                    {error && !loading && (
                        <div style={{ padding:"40px 20px", textAlign:"center", color:"#e65d5d" }}>
                            <div style={{ fontSize:"0.88rem", fontWeight:600 }}>{error}</div>
                            <button onClick={load} style={{
                                marginTop:12, padding:"7px 18px", borderRadius:8,
                                background:"#f1f5f9", border:"1px solid #e2e8f0",
                                cursor:"pointer", fontSize:"0.8rem", fontWeight:600, color:"#475569",
                            }}>Retry</button>
                        </div>
                    )}

                    {!loading && !error && detail && (
                        <>
                            <div className="stat-pills-grid">
                                <StatPill
                                    icon={Package}      label="Pending"
                                    value={detail.ongoing_count ?? partner.pending_orders ?? 0}
                                    color="#f59e0b" bg="#fffbeb"
                                />
                                <StatPill
                                    icon={CheckCircle2} label="Completed"
                                    value={detail.completed_count ?? partner.completed_orders ?? 0}
                                    color="#10b981" bg="#f0fdf4"
                                />
                                {/* Today earnings */}
                                <StatPill
                                    icon={IndianRupee}  label="Today's Earnings"
                                    value={`₹${Number(detail?.today_earnings ?? 0).toLocaleString('en-IN')}`}
                                    color="#6366f1" bg="#f5f3ff"
                                />
                                {/* Today COD */}
                                <StatPill
                                    icon={Banknote}     label="Today's COD"
                                    value={`₹${Number(detail?.today_cod ?? 0).toLocaleString('en-IN')}`}
                                    color="#f97316" bg="#fff7ed"
                                />
                                {detail?.wallet_balance != null && (
                                    <StatPill
                                        icon={Wallet}   label="Wallet Balance"
                                        value={`₹${Number(detail?.wallet_balance ?? 0).toLocaleString('en-IN')}`}
                                        color="#0ea5e9" bg="#f0f9ff"
                                    />
                                )}
                            </div>

                            {/* Map */}
                            <div style={{ padding:"14px 16px 18px" }}>
                                <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:10 }}>
                                    <MapPin size={14} color="#6366f1" />
                                    <span style={{
                                        fontSize:"0.78rem", fontWeight:700, color:"#475569",
                                        textTransform:"uppercase", letterSpacing:"0.06em",
                                    }}>Live Location</span>
                                </div>
                                {hasLocation ? (
                                    <div style={{ height:220, borderRadius:12, overflow:"hidden", border:"1px solid #e2e8f0" }}>
                                        <MapContainer
                                            center={[parseFloat(lat), parseFloat(lng)]}
                                            zoom={15}
                                            style={{ height:"100%", width:"100%" }}
                                            attributionControl={false}
                                        >
                                            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                            <Marker
                                                position={[parseFloat(lat), parseFloat(lng)]}
                                                icon={makeIcon(partner?.heading, "#6366f1")}
                                            >
                                                <Popup>{detail?.name ?? partner.name}</Popup>
                                            </Marker>
                                        </MapContainer>
                                    </div>
                                ) : (
                                    <div style={{
                                        height:100, borderRadius:12,
                                        background:"#f8fafc", border:"1px dashed #e2e8f0",
                                        display:"flex", alignItems:"center", justifyContent:"center",
                                        color:"#94a3b8", fontSize:"0.82rem", fontWeight:600, gap:8,
                                    }}>
                                        <MapPin size={16} color="#cbd5e1" />
                                        Location not available
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

// ── AvailablePartnersDiv ──────────────────────────────────────────────────────
export const AvailablePartnersDiv = ({ id, name, photo, phone, heading, pending_orders, completed_orders, latitude, longitude, loading }) => {
    const [showModal, setShowModal] = useState(false);

    return (
        <>
            <div className={`partner-card ${loading ? "shimmer" : ""}`} style={{ position:"relative" }}>
                {loading ? (
                    <div style={{ padding:"20px" }}>
                        <div className="skeleton-line w-60 mb-10" />
                        <div className="skeleton-line w-50 mb-10" />
                        <div className="skeleton-line w-40" />
                    </div>
                ) : (
                    <>
                        <p className="pending">Pending Orders: {pending_orders}</p>
                        <p className="completed">Completed Orders: {completed_orders}</p>
                        <h4>{name}</h4>

                        <button
                            onClick={() => setShowModal(true)}
                            title="View details"
                            style={{
                                position:"absolute", top:10, right:10,
                                width:30, height:30, borderRadius:8,
                                background:"#f1f5f9", border:"1px solid #e2e8f0",
                                display:"flex", alignItems:"center", justifyContent:"center",
                                cursor:"pointer", transition:"all 0.18s", padding:0,
                            }}
                            onMouseEnter={e => {
                                e.currentTarget.style.background = "#e0e7ff";
                                e.currentTarget.style.borderColor = "#c7d2fe";
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.background = "#f1f5f9";
                                e.currentTarget.style.borderColor = "#e2e8f0";
                            }}
                        >
                            <Info size={14} color="#6366f1" />
                        </button>
                    </>
                )}
            </div>

            {showModal && (
                <DeliveryPartnerModal
                    partner={{ id, name, phone, heading, photo, pending_orders, completed_orders, latitude, longitude }}
                    onClose={() => setShowModal(false)}
                />
            )}
        </>
    );
};

export default AvailablePartnersDiv;